import type { PropsWithChildren, ReactNode } from "react";

import { CurrentPagePresence } from "@/components/CurrentPagePresence";
import { QuickSwitcher } from "@/components/QuickSwitcher";
import { UserMenu } from "@/components/UserMenu";
import { cn } from "@/lib/utils";

export type AccountPageShellProps = PropsWithChildren<{
  /** Page title under the SGS Hub eyebrow (for example Settings or Notifications). */
  title: ReactNode;
  /** Tailwind max-width class for the header and main column. Defaults to max-w-2xl. */
  widthClassName?: string;
  /** When true, show a centered loading state instead of children. */
  loading?: boolean;
}>;

/**
 * Standalone account-area shell (header with SGS Hub title + UserMenu, constrained main column).
 * Used by Settings and Notifications outside module Layout.
 */
export function AccountPageShell({
  title,
  widthClassName = "max-w-2xl",
  loading = false,
  children,
}: AccountPageShellProps): JSX.Element {
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <p className="text-sm text-slate-600">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <div className={cn("mx-auto flex w-full items-center justify-between gap-3", widthClassName)}>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">SGS Hub</p>
            <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <CurrentPagePresence />
            <QuickSwitcher />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className={cn("mx-auto w-full flex-1 space-y-6 px-4 py-6", widthClassName)}>{children}</main>
    </div>
  );
}
